import "@/App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { LanguageProvider } from "@/i18n";
import { AuthProvider } from "@/auth/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
// Eager: landing (first paint) + OAuth callback (runs before route resolution)
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";

// Code-split: every other route loads its own chunk on demand
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Catalogue = lazy(() => import("@/pages/Catalogue"));
const SectionPage = lazy(() => import("@/pages/SectionPage"));
const BoutiquesPage = lazy(() => import("@/pages/BoutiquesPage"));
const BoutiquePage = lazy(() => import("@/pages/BoutiquePage"));
const TrackOrder = lazy(() => import("@/pages/TrackOrder"));
const BrandsShowcase = lazy(() => import("@/pages/BrandsShowcase"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const PublicProfile = lazy(() => import("@/pages/PublicProfile"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const Payment = lazy(() => import("@/pages/Payment"));
const Events = lazy(() => import("@/pages/Events"));
const EventDetail = lazy(() => import("@/pages/EventDetail"));
const TicketOrder = lazy(() => import("@/pages/TicketOrder"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaymentCancel })));
const PaystackCallback = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaystackCallback })));
const FlutterwaveCallback = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.FlutterwaveCallback })));
const BoutiqueCallback = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.BoutiqueCallback })));

function RouteFallback() {
  return (
    <div
      className="min-h-screen bg-[#050505] flex items-center justify-center"
      data-testid="route-loading"
    >
      <div className="h-8 w-8 rounded-full border-2 border-[#d4af37]/25 border-t-[#d4af37] animate-spin" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Process the OAuth callback synchronously (before ProtectedRoute / auth checks run)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/catalogue" element={<Catalogue />} />
        <Route path="/mannequins" element={<SectionPage category="model" />} />
        <Route path="/photographes" element={<SectionPage category="photographer" />} />
        <Route path="/stylistes" element={<SectionPage category="stylist" />} />
        <Route path="/marques" element={<SectionPage category="brand" />} />
        <Route path="/boutiques" element={<BoutiquesPage />} />
        <Route path="/boutiques/:handle" element={<BoutiquePage />} />
        <Route path="/suivi/:token" element={<TrackOrder />} />
        <Route path="/espace-marques" element={<BrandsShowcase />} />
        <Route path="/brands" element={<BrandsShowcase />} />
        <Route path="/evenements" element={<Events />} />
        <Route path="/evenements/:eventId" element={<EventDetail />} />
        <Route path="/billets/:orderId" element={<TicketOrder />} />
        <Route path="/talent/:handle" element={<PublicProfile />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/*"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />
        <Route path="/pay/:bookingId" element={<Payment />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/paystack" element={<PaystackCallback />} />
        <Route path="/payment/flutterwave" element={<FlutterwaveCallback />} />
        <Route path="/payment/boutique" element={<BoutiqueCallback />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" theme="dark" />
        </AuthProvider>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
